import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Calendar, AlertCircle, CheckCircle } from 'lucide-react';

const StudentOverviewPage = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [child, setChild] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    loadData();
  }, [childId]);

  const loadData = async () => {
    try {
      const [childData, assignmentsData, subjectsData] = await Promise.all([
        api.getChildren(),
        api.getAssignments(),
        api.getSubjects()
      ]);

      const selectedChild = childData.find(c => c.id === childId);
      setChild(selectedChild);

      // Filter assignments for this child
      const childAssignments = assignmentsData.filter(a => a.child_id === childId);
      setAssignments(childAssignments);
      setSubjects(subjectsData);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load student data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? `${subject.icon} ${subject.name}` : 'Unknown Subject';
  };

  const isToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isOverdue = (dateString, status) => {
    if (status === 'submitted' || status === 'graded') return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Categorize assignments
  const dueToday = assignments.filter(a => 
    isToday(a.due_date) && a.status !== 'submitted' && a.status !== 'graded'
  );

  const overdue = assignments.filter(a => 
    isOverdue(a.due_date, a.status)
  );

  const returned = assignments.filter(a => 
    a.needs_revision === true || a.send_back_notes
  ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  const graded = assignments.filter(a => 
    a.status === 'graded'
  ).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xl text-slate-600">Loading...</p>
      </div>
    );
  }

  if (!child) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xl text-slate-600">Student not found</p>
      </div>
    );
  }

  const AssignmentCard = ({ assignment, showDate = true }) => (
    <div className="p-4 bg-white rounded-lg border hover:border-purple-300 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-semibold text-slate-900">{assignment.title}</h4>
          <p className="text-sm text-slate-600 mt-1">{getSubjectName(assignment.subject_id)}</p>
          {assignment.description && (
            <p className="text-xs text-slate-500 mt-1">{assignment.description}</p>
          )}
          {showDate && (
            <p className="text-xs text-slate-500 mt-2">
              Due: {new Date(assignment.due_date).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 ml-4">
          <Badge className={
            assignment.status === 'graded' 
              ? 'bg-green-100 text-green-700'
              : assignment.status === 'submitted'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-slate-100 text-slate-700'
          }>
            {assignment.status === 'graded' ? 'Graded' : 
             assignment.status === 'submitted' ? 'Submitted' : 'Assigned'}
          </Badge>
          <span className="text-sm font-medium text-slate-600">
            {assignment.points_possible} pts
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
              {child.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{child.name}</h1>
              <p className="text-slate-600">{child.grade}</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-orange-200 bg-orange-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-600" />
                Due Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-600">{dueToday.length}</p>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                Overdue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">{overdue.length}</p>
            </CardContent>
          </Card>

          <Card className="border-yellow-200 bg-yellow-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                Returned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-yellow-600">{returned.length}</p>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Graded
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{graded.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Due Today Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              Due Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dueToday.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No assignments due today! 🎉</p>
            ) : (
              <div className="space-y-3">
                {dueToday.map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} showDate={false} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Overdue Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Overdue
            </CardTitle>
          </CardHeader>
          <CardContent>
            {overdue.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No overdue assignments! Great work! ✅</p>
            ) : (
              <div className="space-y-3">
                {overdue.map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Returned for Resubmission Section */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              Returned for Resubmission
            </CardTitle>
          </CardHeader>
          <CardContent>
            {returned.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No assignments returned for resubmission.</p>
            ) : (
              <div className="space-y-3">
                {returned.map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Graded Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Recently Graded
            </CardTitle>
          </CardHeader>
          <CardContent>
            {graded.length === 0 ? (
              <p className="text-slate-500 text-center py-4">No graded assignments yet.</p>
            ) : (
              <div className="space-y-3">
                {graded.slice(0, 10).map(assignment => (
                  <AssignmentCard key={assignment.id} assignment={assignment} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentOverviewPage;
